import { useEffect, useState } from 'react'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import type { CarDoc, DriverDoc } from '@racedash/shared'
import { db } from '../lib/firebase'

export interface Car extends CarDoc {
  id: string
}
export interface Driver extends DriverDoc {
  id: string
}

/**
 * The cars this organisation runs.
 *
 * Ordered by number, which is how a team refers to them out loud. Stored as a
 * string so "7" and "07" stay distinct, which means the ordering is
 * lexicographic — acceptable for a handful of cars, and the alternative
 * silently merges two entries a timekeeper considers different.
 */
export function useCars(orgId: string | undefined) {
  return useOrgCollection<Car>(orgId, 'cars', 'number')
}

/** The people who drive them. */
export function useDrivers(orgId: string | undefined) {
  return useOrgCollection<Driver>(orgId, 'drivers', 'name')
}

function useOrgCollection<T>(orgId: string | undefined, name: string, sortBy: string) {
  const [items, setItems] = useState<T[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orgId) {
      setItems(null)
      return
    }
    return onSnapshot(
      query(collection(db, 'orgs', orgId, name), orderBy(sortBy)),
      (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T)),
      (e) => setError(e.message),
    )
  }, [orgId, name, sortBy])

  return { items, error }
}

export async function createCar(orgId: string, number: string, label: string) {
  return addDoc(collection(db, 'orgs', orgId, 'cars'), {
    number: number.trim(),
    label: label.trim(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}

export async function updateCar(orgId: string, carId: string, number: string, label: string) {
  return updateDoc(doc(db, 'orgs', orgId, 'cars', carId), {
    number: number.trim(),
    label: label.trim(),
    updatedAt: serverTimestamp(),
  })
}

/**
 * Removing a car from the list.
 *
 * Entries already made against it are left alone: they record what was entered
 * at an event that has happened, and rewriting history to match a change made
 * afterwards would be worse than a dangling reference. The entry carries its
 * own number, so it still reads correctly without the car.
 */
export async function deleteCar(orgId: string, carId: string) {
  return deleteDoc(doc(db, 'orgs', orgId, 'cars', carId))
}

export async function createDriver(orgId: string, name: string, shortName: string) {
  return addDoc(collection(db, 'orgs', orgId, 'drivers'), {
    name: name.trim(),
    shortName: shortName.trim() || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}

export async function updateDriver(
  orgId: string,
  driverId: string,
  name: string,
  shortName: string,
) {
  return updateDoc(doc(db, 'orgs', orgId, 'drivers', driverId), {
    name: name.trim(),
    shortName: shortName.trim() || null,
    updatedAt: serverTimestamp(),
  })
}

export async function deleteDriver(orgId: string, driverId: string) {
  return deleteDoc(doc(db, 'orgs', orgId, 'drivers', driverId))
}
